import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import Seo from "@/components/Seo";
import { ShieldCheck, Loader2, Lock } from "lucide-react";

export default function AdminLoginPage() {
  const { adminLogin, adminVerifyOtp } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [delivery, setDelivery] = useState(null);

  // Poll Twilio delivery state for the OTP message until a final status is known (never exposes the code).
  useEffect(() => {
    if (!challenge?.challenge_id) return undefined;
    setDelivery(challenge.delivery || null);
    let tries = 0;
    const id = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/auth/admin-login/status?challenge_id=${encodeURIComponent(challenge.challenge_id)}`);
        setDelivery(data.delivery);
        if (data.delivery?.final || tries >= 12) clearInterval(id);
      } catch { if (tries >= 12) clearInterval(id); }
    }, 2500);
    return () => clearInterval(id);
  }, [challenge?.challenge_id]); // eslint-disable-line
  const [code, setCode] = useState("");

  const fail = (err) => {
    const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
    setError(msg);
    toast.error(msg);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await adminLogin(form.email, form.password);
      if (data.otp_required) {
        setChallenge(data);
        setCode("");
        toast.message("Verification code queued for WhatsApp delivery");
        return;
      }
      toast.success("Welcome, admin");
      navigate("/dashboard");
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminVerifyOtp(challenge.challenge_id, code);
      toast.success("Welcome, admin");
      navigate("/dashboard");
    } catch (err) {
      fail(err);
      if (err.response?.status === 400 || err.response?.status === 429) setChallenge(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white flex items-center justify-center px-4" data-testid="admin-login-page">
      <Seo title="Admin sign in" path="/ez-admin-x9f3q7" noindex />
      <div className="absolute inset-0 grid-texture opacity-30" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md rounded-3xl border border-white/10 bg-[#131C2E] p-8">
        <div className="flex items-center justify-between mb-6">
          <Logo />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 px-3 py-1 text-xs font-bold text-amber-300">
            <ShieldCheck className="h-3.5 w-3.5" /> Admin
          </span>
        </div>
        <h1 className="font-display text-2xl font-bold">{challenge ? "Enter verification code" : "Administrator sign in"}</h1>
        <p className="mt-1 text-sm text-slate-400">
          {challenge ? `A 6-digit code is being sent to WhatsApp ${challenge.sent_to?.join(", ")}. It expires in 5 minutes.` : "Restricted access. Authorised staff only."}
        </p>

        {challenge ? (
          <form onSubmit={verify} className="mt-6 space-y-4" data-testid="admin-otp-form">
            <input data-testid="admin-otp-input" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required autoFocus value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••"
              className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-center font-mono-data text-2xl tracking-[0.5em] text-white placeholder:text-slate-600 outline-none focus:border-amber-400/50" />
            <DeliveryState d={delivery} />
            {error && <p data-testid="admin-login-error" className="text-sm text-rose-400">{error}</p>}
            <button data-testid="admin-otp-submit" type="submit" disabled={loading || code.length !== 6}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-400 px-6 py-3.5 text-sm font-bold text-[#1a1206] transition-transform hover:scale-[1.01] disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4" /> Verify &amp; sign in</>}
            </button>
            <button type="button" data-testid="admin-otp-back" onClick={() => { setChallenge(null); setError(""); }}
              className="w-full text-center text-xs text-slate-500 hover:text-slate-300">Didn't get it? Sign in again to resend</button>
          </form>
        ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-300">Admin email</label>
            <input data-testid="admin-email-input" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none focus:border-amber-400/50" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-300">Password</label>
            <input data-testid="admin-password-input" type="password" required value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none focus:border-amber-400/50" />
          </div>
          {error && <p data-testid="admin-login-error" className="text-sm text-rose-400">{error}</p>}
          <button data-testid="admin-login-submit" type="submit" disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-400 px-6 py-3.5 text-sm font-bold text-[#1a1206] transition-transform hover:scale-[1.01] disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4" /> Sign in to Admin</>}
          </button>
        </form>
        )}
        <button onClick={() => navigate("/auth")} className="mt-6 w-full text-center text-xs text-slate-500 hover:text-slate-300">
          ← Student / Counsellor / Parent sign in
        </button>
      </motion.div>
    </div>
  );
}


const LABEL = { queued: "Queued at WhatsApp…", accepted: "Queued at WhatsApp…", sent: "Sent — waiting for WhatsApp delivery…", delivered: "Delivered to your WhatsApp", read: "Delivered to your WhatsApp",
  failed: "WhatsApp delivery failed", undelivered: "WhatsApp delivery failed", error: "WhatsApp send error", not_configured: "WhatsApp channel not configured", unknown: "Checking delivery status…" };

const DeliveryState = ({ d }) => {
  if (!d) return null;
  const bad = ["failed", "undelivered", "error", "not_configured"].includes(d.status);
  const ok = ["delivered", "read"].includes(d.status);
  return (
    <div data-testid="otp-delivery-status" data-status={d.status} className={`rounded-xl border px-3 py-2 text-xs ${bad ? "border-rose-400/40 bg-rose-400/5 text-rose-200" : ok ? "border-emerald-400/40 bg-emerald-400/5 text-emerald-200" : "border-white/10 bg-white/[0.03] text-slate-300"}`}>
      <p className="font-semibold">{LABEL[d.status] || d.status}{d.error_code ? ` (Twilio ${d.error_code})` : ""}</p>
      {d.hint && <p data-testid="otp-delivery-hint" className="mt-1">{d.hint}</p>}
      {["63015", "63016"].includes(String(d.error_code)) && <p className="mt-1 text-slate-400">Re-join: send <b>join dried-large</b> from the admin WhatsApp number to +1 415 523 8886, then sign in again.</p>}
    </div>
  );
};